import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { sendEvent } from 'reaxl-analytics';
import { calculatePersonalPayment, getInterestRate, formattedNumber } from 'atc-js';
import calculateTotalPayment from '@/utilities/calculateTotalPayment';
import { callToActionAnalytics } from '@/analytics';
import { convertPrice } from '@/utilities';
import { defaultMonthlyPrice } from '@/constants';
import { paymentsDuck } from '@/ducks';

const calculateBudget = (price, budgetSummaryType, paymentsInfo) => {
    const convertedPrice = convertPrice(price);
    let calculatedBudget = convertedPrice;
    if (budgetSummaryType === 'monthly') {
        calculatedBudget = calculateTotalPayment(convertedPrice, paymentsInfo, getInterestRate(paymentsInfo.interestRate));
    }
    return {
        price: convertedPrice,
        calculatedBudget,
    };
};

const handleZipChange = (event, zip, setZipValue) => {
    const newZip = event.target.value;
    if (newZip && newZip.length === 5 && typeof zip.onValidation === 'function') {
        zip.onValidation(event, newZip).then((isValidZip) => {
            if (isValidZip) {
                setZipValue(newZip);
                sendEvent({
                    name: 'zipOnChange',
                    event,
                    data: { zipcode: newZip },
                });
            }
        }).catch((error) => {
            console.error('fetchValidateZip (getMarket) error', error); // eslint-disable-line no-console
        });
    }
};

const useBrowseByBudget = (additionalParams = {}, withPixallData) => {
    const paymentsInfo = useSelector(paymentsDuck.selectors.getPaymentsInfo) || {};
    const {
        budget,
        interestRate,
        interactions,
        monthlyBudget,
    } = paymentsInfo;

    const {
        isBudgetTab,
        zip,
        openSRPFromBudgetTab,
        displayShowWalletModalOnSRP = '',
    } = additionalParams;

    const dispatch = useDispatch();

    const [budgetSummaryType, setBudgetSummaryType] = useState('monthly');
    const [monthlyPayment, setMonthlyPayment] = useState(defaultMonthlyPrice);
    const defaultBudgetPrice = calculateTotalPayment(defaultMonthlyPrice, paymentsInfo, getInterestRate(paymentsInfo.interestRate));

    // only for BudgetTab
    const [zipValue, setZipValue] = useState(zip && zip.value);

    const formattedMonthlyBudget = budget !== null
        ? `$${monthlyPayment.toLocaleString('en-US')}`
        : `$${defaultMonthlyPrice}`;

    const formattedMaxPayment = budget !== null
        ? `$${budget.toLocaleString('en-US')}`
        : `$${formattedNumber({ value: defaultBudgetPrice })}`;

    useEffect(() => {
        const monthly = Math.floor(calculatePersonalPayment(budget, paymentsInfo, getInterestRate(interestRate)));
        setMonthlyPayment(monthly);

        if (interactions?.budgetSummaryType) {
            setBudgetSummaryType(interactions.budgetSummaryType);
        }

    }, [budget, paymentsInfo, interestRate, interactions]);

    const populatePriceInputValue = () => {
        if (budgetSummaryType === 'monthly') {
            return formattedMonthlyBudget;
        }
        return formattedMaxPayment;
    };

    const handleInputBlur = useCallback((e) => {
        if (e.target.value === '' || e.target.value === '$') return;

        const {
            price,
            calculatedBudget,
        } = calculateBudget(e.target.value, budgetSummaryType, paymentsInfo);

        dispatch(paymentsDuck?.creators?.updatePaymentsInfo({
            budget: calculatedBudget,
            ...(budgetSummaryType === 'monthly' && { monthlyBudget: price }),
        }));
    }, [budgetSummaryType, paymentsInfo]);

    const handleBudgetOnChange = useCallback(async (e) => {
        handleInputBlur(e);
    }, [handleInputBlur]);

    // only for BudgetTab
    const handleZipChangeCallback = useCallback((event) => {
        handleZipChange(event, zip, setZipValue);
    }, [zip]);

    const getPrice = budgetSummaryType === 'monthly' ? monthlyBudget : budget;

    const handleSubmit = async (e) => {
        const priceInput = e.price || getPrice.toString();

        const {
            calculatedBudget,
            price,
        } = calculateBudget(priceInput, e.searchByBudgetCriteria, paymentsInfo);

        // Note: Analytics asked that Variant 1 and 2 share the same tag, even though Variant 2 opens the modal
        // they will filter by testid
        if (isBudgetTab) {
            callToActionAnalytics.fireSearchSubmit({ zip: zipValue, ctaText: 'search_budget' });
        } else {
            callToActionAnalytics.fireBrowseByBudgetWalletContinue(e, {
                coTxtUrl: e.searchByBudgetCriteria,
                withPixallData,
            });
        }

        // BrowseByBudgetContainer and BudgetTab (variant 2) open the modal
        await dispatch(paymentsDuck?.creators?.updatePaymentsInfo({
            budget: calculatedBudget,
            ...(e.searchByBudgetCriteria === 'monthly' && { monthlyBudget: price }),
        }));

        dispatch(paymentsDuck?.creators?.updateMyWallet({
            displayModal: !openSRPFromBudgetTab,
            selectedPanelName: 'budget',
        }));

        dispatch(paymentsDuck?.creators?.updateMyWalletInteractions({
            searchByBudgetPrice: price,
            budgetSummaryType: e.searchByBudgetCriteria,
            focusBudgetInput: true,
            showPaymentInFilter: true,
        }));

        // BudgetTab (variant 1) goes to SRP - must update redux before submitting..
        if (openSRPFromBudgetTab) {
            const url = `/cars-for-sale/all-cars/cars-under-${calculatedBudget}?zip=${zipValue}${displayShowWalletModalOnSRP}`;
            window.location.assign(url);
        }
    };

    return {
        populatePriceInputValue,
        handleInputBlur,
        handleZipChangeCallback,
        handleSubmit,
        setBudgetSummaryType,
        budgetSummaryType,
        formattedMonthlyBudget,
        formattedMaxPayment,
        handleBudgetOnChange,
    };
};

export default useBrowseByBudget;
